/**
 * ConversationsScreen — l'elenco COMPLETO delle conversazioni, fuori dalla home.
 *
 * La home (InboxScreen) è una dashboard: notifiche + scrivi + azioni, niente
 * lista di chat. Qui invece vive la lista vera e propria, con:
 *   - raggruppamento "Da vedere" (richieste in arrivo + non lette) in cima
 *   - filtro per identità (solo se hai sigil derivati)
 *   - "solo non lette"
 *   - selezione multipla + rimozione in blocco
 * Tap su una riga → apre il thread (/chat/:id).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box, Cursor } from '../components/Frame';
import { ChatRow, isNewChat } from '../components/ChatRow';
import { roomsVisible } from '../lib/features';
import { useStore } from '../lib/store';
import { callFn } from '../lib/firebase';
import { hideChat, subscribeMySignal } from '../lib/messaging';
import { getCachedChatList, setCachedChatList } from '../lib/sessionCache';

export default function ConversationsScreen() {
  const navigate = useNavigate();
  const { sigil, uid, firebaseUser, derivedKeys } = useStore();
  const myFirebaseUid = firebaseUser?.uid;

  const [chats, setChats] = useState(() => getCachedChatList(myFirebaseUid));
  const [error, setError] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [filterSigil, setFilterSigilState] = useState(() => {
    try { return localStorage.getItem('nullum.inboxFilter.v1') || null; } catch { return null; }
  });
  function setFilterSigil(v) {
    setFilterSigilState(v);
    try {
      if (v) localStorage.setItem('nullum.inboxFilter.v1', v);
      else localStorage.removeItem('nullum.inboxFilter.v1');
    } catch {}
  }
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchChats = useCallback(async () => {
    if (!myFirebaseUid) return;
    try {
      const res = await callFn('listMyChats')({ nullumUid: uid });
      const payload = res.data;
      if (payload?.ok) {
        const items = (payload.chats || []).map((c) => ({ ...c, callerUid: myFirebaseUid }));
        setChats(items);
        setCachedChatList(myFirebaseUid, items);
        setError(null);
      }
    } catch (e) { console.warn('listMyChats failed', e); }
  }, [myFirebaseUid, uid]);

  // Real-time: un listener sul proprio doc utente sveglia la fetch solo quando
  // c'è attività vera. Poll lento (30s) come rete di sicurezza.
  useEffect(() => {
    if (!myFirebaseUid) return;
    let alive = true;
    fetchChats();
    let lastTick = 0;
    const off = subscribeMySignal(myFirebaseUid, (millis) => {
      if (!alive) return;
      if (millis && millis === lastTick) return;
      lastTick = millis;
      fetchChats();
    });
    const slow = setInterval(() => { if (alive) fetchChats(); }, 30000);
    return () => { alive = false; try { off && off(); } catch {} clearInterval(slow); };
  }, [myFirebaseUid, fetchChats]);

  // ── Lista visibile: filtro identità + "solo non lette" + ordina per recenza ─
  const visibleChats = (chats || [])
    .filter((c) => {
      if (filterSigil && Object.keys(derivedKeys || {}).length > 0
        && (c.mySigilInChat || sigil) !== filterSigil) return false;
      if (unreadOnly && !isNewChat(c)) return false;
      return true;
    })
    .sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0));

  const initiatorOf = (c) => c.initiator ?? c.requestedBy ?? null;
  const isIncomingRequest = (c) => c.status === 'pending' && !!initiatorOf(c) && initiatorOf(c) !== c.callerUid;
  const needsAttention = (c) => isIncomingRequest(c) || isNewChat(c);
  const toSeeChats = visibleChats.filter(needsAttention);
  const restChats = visibleChats.filter((c) => !needsAttention(c));
  const reqCount = visibleChats.filter(isIncomingRequest).length;
  const unreadCount = visibleChats.filter((c) => isNewChat(c) && !isIncomingRequest(c)).length;

  const renderRow = (c) => (
    <ChatRow
      key={c.id}
      chat={c}
      isNew={isNewChat(c)}
      selectMode={selectMode}
      selected={selected.has(c.id)}
      onToggleSelect={toggleSelect}
      onClick={() => navigate(`/chat/${c.id}`)}
      onHide={async () => {
        try { await hideChat(c.id, 'self'); await fetchChats(); }
        catch (e) { setError(e?.message || 'rimozione fallita'); }
      }}
      onDeleteBoth={async () => {
        try { await hideChat(c.id, 'both'); await fetchChats(); }
        catch (e) { setError(e?.message || 'cancellazione fallita'); }
      }}
    />
  );

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function exitSelect() { setSelectMode(false); setSelected(new Set()); }
  function selectAllVisible() {
    const ids = visibleChats.map((c) => c.id);
    setSelected((prev) => (ids.length > 0 && ids.every((id) => prev.has(id)) ? new Set() : new Set(ids)));
  }
  async function bulkRemove() {
    if (selected.size === 0) return;
    if (!confirm(`Rimuovere ${selected.size} chat dalla tua lista?\n\nRestano visibili all'altra persona; le ritrovi riaprendone una con lo stesso sigil.`)) return;
    setBulkBusy(true);
    setError(null);
    try {
      for (const id of selected) {
        // eslint-disable-next-line no-await-in-loop
        await hideChat(id, 'self');
      }
      await fetchChats();
      exitSelect();
    } catch (e) {
      setError(e?.message || 'rimozione multipla fallita');
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <Frame statusLeft="CONVERSAZIONI" statusRight="5G">
      <Header title="// Conversazioni" crumb="◀ home" onCrumbClick={() => navigate('/inbox')} />

      {error && (
        <Box variant="danger" className="text-[16px] mb-3">errore: {error}</Box>
      )}

      {roomsVisible(sigil) && (
        <button
          onClick={() => navigate('/rooms')}
          className="w-full border border-ink-dim rounded-lg text-ink text-[14px] py-2 mb-3 hover:border-ink transition-colors"
        >
          🚪 Stanze di gruppo
        </button>
      )}

      {/* Filtro per identità: solo se hai sigil derivati. */}
      {Object.keys(derivedKeys || {}).length > 0 && Array.isArray(chats) && (
        <div className="flex gap-2 flex-wrap mb-3 text-[12px]">
          {[
            { key: null, label: 'Tutte' },
            { key: sigil, label: `@${sigil}` },
            ...Object.values(derivedKeys).map((d) => ({ key: d.sigil, label: `@${d.sigil}` })),
          ].map((f) => {
            const forKey = f.key
              ? chats.filter((c) => (c.mySigilInChat || sigil) === f.key)
              : chats;
            const nuove = forKey.filter(isNewChat).length;
            return (
              <button
                key={f.label}
                onClick={() => setFilterSigil(f.key)}
                className={`border rounded-full px-3 py-1 flex items-center gap-1 transition-colors ${filterSigil === f.key ? 'border-ink text-ink bg-ink/10 shadow-glow' : 'border-ink-dim/50 text-ink-dim hover:border-ink-dim hover:bg-ink/[0.04]'}`}
              >
                <span>{f.label}</span>
                {nuove > 0 && (
                  <span className="text-[10px] text-amber border border-amber rounded-full px-1.5 leading-tight">{nuove}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {chats && chats.length > 0 && (
        <div className="flex items-center justify-between mb-2 text-[13px]">
          {!selectMode ? (
            <>
              <button onClick={() => setSelectMode(true)} className="text-ink-dim hover:text-ink">
                ✓ Seleziona
              </button>
              <button
                onClick={() => setUnreadOnly((v) => !v)}
                className={`flex items-center gap-1.5 ${unreadOnly ? 'text-ink text-glow' : 'text-ink-dim hover:text-ink'}`}
                title="mostra solo le chat con nuovi messaggi"
              >
                <span className={`w-2.5 h-2.5 rounded-full ${unreadOnly ? 'bg-ink shadow-glow' : 'border border-ink-dim'}`} />
                solo non lette
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <button onClick={selectAllVisible} className="text-ink-dim hover:text-ink">
                  {visibleChats.length > 0 && visibleChats.every((c) => selected.has(c.id)) ? 'Deseleziona tutte' : 'Seleziona tutte'}
                </button>
                <span className="text-ink-faint">{selected.size} sel.</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={bulkRemove}
                  disabled={bulkBusy || selected.size === 0}
                  className="text-red-400 hover:text-red-300 disabled:opacity-40"
                >
                  {bulkBusy ? '…' : `− Rimuovi (${selected.size})`}
                </button>
                <button onClick={exitSelect} className="text-ink-dim hover:text-ink">Annulla</button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex-1 min-h-[300px]">
        {chats === null ? (
          <div className="text-center text-ink-dim text-[16px] py-8">
            loading<Cursor />
          </div>
        ) : chats.length === 0 ? (
          <div className="text-center text-ink-dim text-[16px] py-10">
            <div className="text-[40px] mb-3 opacity-60">💬</div>
            <p className="text-ink text-glow">Nessuna conversazione</p>
            <p className="text-ink-faint mt-2">
              torna alla home e tocca &laquo;Scrivi un messaggio&raquo; per iniziarne una.
            </p>
            <Btn variant="primary" className="mt-4" onClick={() => navigate('/new')}>✍️ Scrivi un messaggio</Btn>
          </div>
        ) : visibleChats.length === 0 ? (
          <div className="text-center text-ink-dim text-[14px] py-10">
            {unreadOnly ? 'nessuna chat con nuovi messaggi ✓' : 'nessuna chat per questo filtro'}
          </div>
        ) : (
          <>
            {toSeeChats.length > 0 && (
              <>
                <div className="flex items-center flex-wrap gap-x-2 text-[12px] mb-2 mt-1">
                  <span className="text-amber">🔔 Da vedere</span>
                  <span className="text-ink-dim">
                    {[
                      reqCount > 0 ? `${reqCount} in arrivo` : null,
                      unreadCount > 0 ? `${unreadCount} non lett${unreadCount === 1 ? 'o' : 'i'}` : null,
                    ].filter(Boolean).join(' · ')}
                  </span>
                </div>
                {toSeeChats.map(renderRow)}
              </>
            )}
            {restChats.length > 0 && (
              <>
                {toSeeChats.length > 0 && (
                  <div className="text-[12px] text-ink-dim mb-2 mt-4">💬 Tutte le conversazioni</div>
                )}
                {restChats.map(renderRow)}
              </>
            )}
          </>
        )}
      </div>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Home</Btn>
      </Footer>
    </Frame>
  );
}
